import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import Swal from 'sweetalert2';
import {
  BillingProcess,
  BillingProcessItem,
  BillingProcessSourceType,
} from 'src/app/models/billing-process';
import { BillingProcessService } from 'src/app/services/billing-process.service';
import { OperationalConfigService } from 'src/app/services/config/operational-config.service';
import { MessageUtil } from 'src/app/utils/message.util';

type GroupKey = 'BOAT_SERVICE' | 'CUSTOMER_SERVICE' | 'FRANCHISE';

interface GroupSummary {
  key: GroupKey;
  title: string;
  icon: string;
  available: number;
  totalAvailable: number;
  selected: number;
  totalSelected: number;
  skipped: number;
}

@Component({
  selector: 'app-wizard-step-services',
  templateUrl: './wizard-step-services.component.html',
  styleUrls: ['./wizard-step-services.component.scss'],
})
export class WizardStepServicesComponent implements OnInit {
  @Input() process: BillingProcess;
  @Output() next = new EventEmitter<void>();

  items: BillingProcessItem[] = [];
  loading = false;
  reloading = false;
  usesFranchise = false;

  personalizeOpen: GroupKey | null = null;
  showOnlyEligible = true;
  selectedProductFilter: Array<number | null> = [];

  constructor(
    private billingProcessService: BillingProcessService,
    private operationalConfigService: OperationalConfigService,
    private spinner: NgxSpinnerService,
    private messageService: MessageUtil,
  ) {}

  async ngOnInit(): Promise<void> {
    try {
      this.usesFranchise = await this.operationalConfigService.isYatchClub();
    } catch {
      this.usesFranchise = false;
    }
    this.loadItems(true);
  }

  private loadItems(autoLoadIfEmpty: boolean): void {
    this.loading = true;
    this.spinner.show();
    this.billingProcessService.listItems(this.process.id).subscribe({
      next: (items) => {
        if (autoLoadIfEmpty && (!items || items.length === 0)) {
          this.reload(true);
          return;
        }
        this.items = items ?? [];
        this.spinner.hide();
        this.loading = false;
      },
      error: (err) => {
        this.spinner.hide();
        this.loading = false;
        this.messageService.generateMessage(
          'error',
          'SUMMARY.ERROR',
          err?.error?.message ?? 'Erro ao carregar itens.',
        );
      },
    });
  }

  reload(skipConfirm: boolean = false): void {
    const proceed = () => this.executeReload();

    if (skipConfirm || this.items.length === 0) {
      proceed();
      return;
    }

    Swal.fire({
      title: 'Recarregar elegíveis',
      text: 'Esta ação apaga todos os itens carregados (incluindo qualquer '
        + 'seleção personalizada) e gera a lista novamente a partir das regras '
        + 'atuais. Deseja continuar?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sim, recarregar',
      cancelButtonText: 'Cancelar',
      reverseButtons: true,
    }).then((result) => {
      if (result.value) {
        proceed();
      }
    });
  }

  private executeReload(): void {
    this.reloading = true;
    this.loading = true;
    this.spinner.show();
    this.billingProcessService.loadEligibleItems(this.process.id).subscribe({
      next: (items) => {
        this.items = items ?? [];
        this.spinner.hide();
        this.loading = false;
        this.reloading = false;
      },
      error: (err) => {
        this.spinner.hide();
        this.loading = false;
        this.reloading = false;
        this.messageService.generateMessage(
          'error',
          'SUMMARY.ERROR',
          err?.error?.message ?? 'Erro ao recarregar itens.',
        );
      },
    });
  }

  toggle(item: BillingProcessItem): void {
    if (this.isLocked(item)) return;
    const newSelected = !item.selected;
    this.billingProcessService
      .updateItemSelection(this.process.id, item.id, newSelected)
      .subscribe({
        next: (updated) => {
          item.selected = updated.selected;
        },
        error: (err) => {
          this.messageService.generateMessage(
            'error',
            'SUMMARY.ERROR',
            err?.error?.message ?? 'Erro ao atualizar seleção.',
          );
        },
      });
  }

  toggleAll(group: GroupKey, select: boolean): void {
    const targets = this.itemsByGroup(group).filter((i) => !this.isLocked(i) && i.selected !== select);
    targets.forEach((item) => {
      this.billingProcessService
        .updateItemSelection(this.process.id, item.id, select)
        .subscribe({
          next: (updated) => {
            item.selected = updated.selected;
          },
          error: (err) => {
            this.messageService.generateMessage(
              'error',
              'SUMMARY.ERROR',
              err?.error?.message ?? 'Erro ao atualizar seleção.',
            );
          },
        });
    });
  }

  openPersonalize(group: GroupKey): void {
    if (!this.itemsByGroup(group).length) return;
    this.showOnlyEligible = true;
    this.selectedProductFilter = [];
    this.personalizeOpen = group;
  }

  closePersonalize(): void {
    this.personalizeOpen = null;
  }

  isLocked(item: BillingProcessItem): boolean {
    return !!item.alreadyGenerated || !!item.emitterBlocked
      || item.executionStatus === 'SKIPPED';
  }

  isInfoOnly(item: BillingProcessItem): boolean {
    if (!item.serviceId) return true;
    if (!item.customers || item.customers.length === 0) return true;
    const total = Number(item.computedTotal) || 0;
    return total <= 0;
  }

  statusBadge(item: BillingProcessItem): { label: string; tone: 'skipped' | 'warn' | 'muted' } | null {
    if (item.alreadyGenerated) return { label: 'Já gerado', tone: 'skipped' };
    if (item.emitterBlocked) return { label: 'Em outro processo', tone: 'skipped' };
    if (!item.serviceId) return { label: 'Sem serviço ativo', tone: 'warn' };
    if (!item.customers || item.customers.length === 0) return { label: 'Sem proprietário', tone: 'warn' };
    const total = Number(item.computedTotal) || 0;
    if (total <= 0) return { label: 'Sem precificação', tone: 'muted' };
    return null;
  }

  customerSummary(item: BillingProcessItem): string {
    if (!item.customers || item.customers.length === 0) return '';
    return item.customers.map((c) => c.customerName || `#${c.customerId}`).join(', ');
  }

  customerTooltip(item: BillingProcessItem): string {
    if (!item.customers || item.customers.length === 0) return '';
    return item.customers
      .map((c) => {
        const name = c.customerName || `#${c.customerId}`;
        const quota = c.quota != null ? ` (${Number(c.quota).toFixed(0)}%)` : '';
        return `${name}${quota}`;
      })
      .join(' · ');
  }

  itemsByGroup(group: GroupKey): BillingProcessItem[] {
    return this.items
      .filter((i) => i.sourceType === group)
      .sort((a, b) => {
        const ka = (a.boatName || this.customerSummary(a) || '').toLowerCase();
        const kb = (b.boatName || this.customerSummary(b) || '').toLowerCase();
        if (ka !== kb) return ka.localeCompare(kb, 'pt-BR');
        const pa = (a.productName || '').toLowerCase();
        const pb = (b.productName || '').toLowerCase();
        return pa.localeCompare(pb, 'pt-BR');
      });
  }

  groupSummary(group: GroupKey): GroupSummary {
    const list = this.itemsByGroup(group);
    const available = list.filter((i) => !this.isLocked(i));
    const selected = available.filter((i) => i.selected);
    const locked = list.length - available.length;
    return {
      key: group,
      title: this.titleFor(group),
      icon: this.iconFor(group),
      available: available.length,
      totalAvailable: this.sumValue(available),
      selected: selected.length,
      totalSelected: this.sumValue(selected),
      skipped: locked,
    };
  }

  get groups(): GroupKey[] {
    const base: GroupKey[] = ['BOAT_SERVICE', 'CUSTOMER_SERVICE'];
    if (this.usesFranchise) base.push('FRANCHISE');
    return base;
  }

  get summaries(): GroupSummary[] {
    return this.groups.map((g) => this.groupSummary(g));
  }

  get personalizeTitle(): string {
    return this.personalizeOpen ? `Personalizar — ${this.titleFor(this.personalizeOpen)}` : '';
  }

  get personalizeItems(): BillingProcessItem[] {
    if (!this.personalizeOpen) return [];
    let items = this.itemsByGroup(this.personalizeOpen);
    if (this.showOnlyEligible) {
      items = items.filter((i) => !this.isLocked(i) && !this.isInfoOnly(i));
    }
    if (this.selectedProductFilter.length > 0) {
      items = items.filter((i) =>
        this.selectedProductFilter.includes(i.serviceId ?? null));
    }
    return items;
  }

  get productOptions(): Array<{ label: string; value: number | null }> {
    if (!this.personalizeOpen) return [];
    const baseList = this.itemsByGroup(this.personalizeOpen)
      .filter((i) => this.showOnlyEligible ? !this.isLocked(i) && !this.isInfoOnly(i) : true);
    const distinct = new Map<number | null, string>();
    for (const item of baseList) {
      const key = item.serviceId ?? null;
      if (!distinct.has(key)) {
        distinct.set(key, item.productName || 'Sem serviço');
      }
    }
    return Array.from(distinct.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));
  }

  get totalGrandAvailable(): number {
    return this.summaries.reduce((acc, s) => acc + s.totalAvailable, 0);
  }

  get totalGrandSelected(): number {
    return this.summaries.reduce((acc, s) => acc + s.totalSelected, 0);
  }

  get countGrandAvailable(): number {
    return this.summaries.reduce((acc, s) => acc + s.available, 0);
  }

  get countGrandSelected(): number {
    return this.summaries.reduce((acc, s) => acc + s.selected, 0);
  }

  get countGrandSkipped(): number {
    return this.summaries.reduce((acc, s) => acc + s.skipped, 0);
  }

  private titleFor(group: GroupKey): string {
    switch (group) {
      case 'BOAT_SERVICE': return 'Serviços de Embarcação';
      case 'CUSTOMER_SERVICE': return 'Serviços de Cliente';
      case 'FRANCHISE': return 'Franquias';
    }
  }

  private iconFor(group: GroupKey): string {
    switch (group) {
      case 'BOAT_SERVICE': return 'pi pi-shield';
      case 'CUSTOMER_SERVICE': return 'pi pi-users';
      case 'FRANCHISE': return 'pi pi-ticket';
    }
  }

  private sumValue(items: BillingProcessItem[]): number {
    return items.reduce((sum, i) => sum + (Number(i.computedTotal) || 0), 0);
  }
}
